import { useState } from "react";
import { updateTool } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/TextField";
import type { Tool } from "@/api/types";

const HEADING = "text-xs font-semibold tracking-wide text-text/60 uppercase";

/** A tool's free-text notes, which anyone can edit here: what a reader should know that no
 * other field holds (a gap in the standards, who to ask, a caveat). Saving sends the tool's
 * `updated_at` as base_updated_at, so an edit someone else made since it loaded is refused
 * and shown rather than overwritten. A blank note is stored as none. `onChanged` reloads
 * the catalog after a save. */
export function NotesSection({ tool: t, onChanged }: { tool: Tool; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function edit() {
    setDraft(t.note ?? "");
    setError(null);
    setEditing(true);
  }

  async function save() {
    setError(null);
    setBusy(true);
    try {
      await updateTool(t.id, { note: draft.trim() || null }, t.updated_at);
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="mb-1 flex items-center justify-between gap-2">
        <h4 className={HEADING}>Notes</h4>
        {!editing && (
          <Button onClick={edit} className="px-2.5 py-1 text-xs">
            {t.note ? "Edit" : "Add a note"}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="flex flex-col gap-2">
          <TextField aria-label="Notes" multiline rows={4} value={draft} onChange={setDraft} autoFocus />
          {error && <p className="text-sm text-warn">{error}</p>}
          <div className="flex gap-2">
            <Button variant="primary" onClick={save} isDisabled={busy}>
              Save
            </Button>
            <Button onClick={() => setEditing(false)} isDisabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      ) : t.note ? (
        <p className="rounded border border-warn-line bg-warn-bg p-2.5 text-sm whitespace-pre-wrap">{t.note}</p>
      ) : (
        <p className="text-sm text-text/50">No notes yet.</p>
      )}
    </section>
  );
}
